import { ExportInvoiceReports } from '@/features/invoice_reports/components/ExportInvoiceReports';
import { BulkDeleteButtonGroup } from '@/features/invoice_reports/components/List/BulkDeleteButtonGroup';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { AI_OCR_URL } from '../constants';
import styles from './InvoiceReportsButtonGroup.module.scss';
import { ProxyOffButtonGroup } from './ProxyOffBtnGroup/ProxyOffButtonGroup';
import { ProxyOnButtonGroup } from './ProxyOnBtnGroup/ProxyOnButtonGroup';
import { useNavigateOnce, useProxyOnButtonGroupItems } from './hooks';
import type { InvoiceReportsButtonGroupProps } from './types';

const cx = classnames.bind(styles);

/** Export + Import + (ProxyOn: AI OCR + Create new dropdowns | ProxyOff: AI OCR + Create new buttons). Links and ProxyOn menus controlled here via hooks. */
export const InvoiceReportsButtonGroup: FC<InvoiceReportsButtonGroupProps> =
  memo(
    ({
      canAgentEntry,
      reportTypeId,
      containerClassName,
      onExport,
      isExportDisabled,
      isExportLoading,
      onImport,
      onImportProxyApplication,
      selectedCount,
      onBulkDelete,
      isFetching = false,
      noPayeeTooltip,
    }) => {
      const createNewUrl = useMemo(
        () =>
          reportTypeId
            ? `/report_types/${reportTypeId}/invoice_reports/new`
            : null,
        [reportTypeId]
      );

      const aiOcr = useNavigateOnce(AI_OCR_URL);
      const createNew = useNavigateOnce(createNewUrl);

      const proxyOnMenus = useProxyOnButtonGroupItems({
        reportTypeId,
        aiOcrHref: AI_OCR_URL,
        createNewHref: createNewUrl ?? undefined,
        onImport,
        onImportProxyApplication,
        isFetching,
        noPayeeTooltip,
      });

      return (
        <div
          className={cx('root', containerClassName)}
          data-testid='invoice-reports-button-group'
        >
          <div className={cx('buttons')}>
            <BulkDeleteButtonGroup
              selectedCount={selectedCount}
              onBulkDelete={onBulkDelete}
            />
            <div className={cx('actionButtons')}>
              <ExportInvoiceReports
                isDisabled={isExportDisabled || isFetching}
                onExport={onExport}
                isLoading={isExportLoading ?? false}
              />
              {canAgentEntry ? (
                <ProxyOnButtonGroup {...proxyOnMenus} />
              ) : (
                <ProxyOffButtonGroup
                  reportTypeId={reportTypeId}
                  onImport={onImport}
                  onAiOcrClick={aiOcr.handleClick}
                  onCreateNewClick={createNew.handleClick}
                  isAiOcrTriggered={aiOcr.isTriggered}
                  isCreateNewTriggered={createNew.isTriggered}
                  isFetching={isFetching}
                  noPayeeTooltip={noPayeeTooltip}
                />
              )}
            </div>
          </div>
        </div>
      );
    }
  );

InvoiceReportsButtonGroup.displayName = 'InvoiceReportsButtonGroup';
